import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mic, MicOff, Radio, PhoneOff, AlertTriangle, Loader2, MessagesSquare } from 'lucide-react';
import { REX_FACE_URL } from '../constants/federationFaces';

/**
 * RexVoiceLive — live voice channel to Rex via xAI Realtime API (WebSocket).
 * Architect directive · Feb 2026 · Sovereign Spec §IV.
 *
 * Flow:
 *   1. GET  /api/rex/realtime-config       — decides if realtime is available
 *   2. POST /api/rex/realtime-token        — mints ephemeral xAI client_secret
 *   3. Open WebSocket wss://api.x.ai/v1/realtime?model=grok-voice-latest
 *      using the client_secret as subprotocol.
 *   4. AudioContext@24 kHz + AudioWorklet ("pcm-processor") → PCM16 mono frames
 *      → input_audio_buffer.append events on the socket.
 *   5. Server-VAD (default) or explicit input_audio_buffer.commit for PTT.
 *   6. Server events: response.audio.delta plays via Web Audio; transcript
 *      + response deltas update the UI. input_audio_buffer.speech_started
 *      while speaking → response.cancel for barge-in.
 *   7. On WS error/close: fall back to Whisper STT (/api/rex/stt) + Web Speech
 *      TTS + text chat (/api/rex/chat).
 *
 * Props:
 *   compact  — smaller layout for embedded card use (default: full-page)
 */

const API = process.env.REACT_APP_BACKEND_URL;
const XAI_WS_URL = 'wss://api.x.ai/v1/realtime?model=grok-voice-latest';

// ── Audio playback helpers ─────────────────────────────────────────────────
const b64ToPCM16 = (b64) => {
  try {
    const bin = atob(b64);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = bin.charCodeAt(i);
    return new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength / 2);
  } catch { return null; }
};

const pcm16ToFloat32 = (pcm16) => {
  const out = new Float32Array(pcm16.length);
  for (let i = 0; i < pcm16.length; i++) out[i] = pcm16[i] / 0x8000;
  return out;
};

const RexVoiceLive = ({ compact = false }) => {
  // ── State ────────────────────────────────────────────────────────────────
  const [phase, setPhase] = useState('idle');       // idle | connecting | live | fallback | error
  const [mode, setMode] = useState('open-mic');      // open-mic | push-to-talk
  const [micHeld, setMicHeld] = useState(false);     // for push-to-talk
  const [isSpeaking, setIsSpeaking] = useState(false); // Rex is speaking?
  const [transcript, setTranscript] = useState('');
  const [rexReply, setRexReply] = useState('');
  const [errorMsg, setErrorMsg] = useState(null);
  const [fallbackMsg, setFallbackMsg] = useState('');
  const [caps, setCaps] = useState({ realtime_available: false, fallback_stt_available: false });

  // ── Refs (audio + socket) ────────────────────────────────────────────────
  const wsRef = useRef(null);
  const audioCtxRef = useRef(null);
  const workletRef = useRef(null);
  const micStreamRef = useRef(null);
  const playbackTimeRef = useRef(0);          // schedules seamless PCM playback
  const recorderRef = useRef(null);           // MediaRecorder for fallback
  const recorderChunksRef = useRef([]);

  // Mic-enabled derived state
  const micEnabled = phase === 'live' && (mode === 'open-mic' || micHeld);

  // ── Fetch capabilities on mount ──────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API}/api/rex/realtime-config`);
        if (r.ok && alive) setCaps(await r.json());
      } catch { /* keep defaults */ }
    })();
    return () => { alive = false; };
  }, []);

  // ── Tear down helper (mic + ws + audio) ──────────────────────────────────
  const tearDown = useCallback(() => {
    try { workletRef.current?.disconnect(); } catch { /* ignore */ }
    try { micStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    try { audioCtxRef.current?.close(); } catch { /* ignore */ }
    try { wsRef.current?.close(); } catch { /* ignore */ }
    workletRef.current = null;
    micStreamRef.current = null;
    audioCtxRef.current = null;
    wsRef.current = null;
    playbackTimeRef.current = 0;
    setIsSpeaking(false);
  }, []);

  useEffect(() => () => tearDown(), [tearDown]);

  // ── Init audio ctx + worklet + mic ───────────────────────────────────────
  const initAudioPipeline = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    micStreamRef.current = stream;

    const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
    audioCtxRef.current = ctx;

    await ctx.audioWorklet.addModule('/pcm-processor-worklet.js');
    const source = ctx.createMediaStreamSource(stream);
    const worklet = new AudioWorkletNode(ctx, 'pcm-processor');
    workletRef.current = worklet;

    worklet.port.onmessage = (event) => {
      // Only forward frames when mic is currently enabled.
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      // Respect mode/held state — this closure reads the current state via ref-ish
      // pattern; safe because the audio buffer is small and inspected each frame.
      if (!wsRef.current._micEnabled) return;
      const buffer = event.data;
      const bytes = new Uint8Array(buffer);
      let bin = '';
      for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
      const b64 = btoa(bin);
      ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: b64 }));
    };

    source.connect(worklet);
    // Do not connect to destination — we don't want mic monitored back.
  }, []);

  // Keep the mic-enabled flag on the socket so audio thread can inspect it.
  useEffect(() => {
    if (wsRef.current) wsRef.current._micEnabled = micEnabled;
  }, [micEnabled]);

  // ── Play PCM16 24 kHz frame via Web Audio ───────────────────────────────
  const enqueuePlayback = useCallback((pcm16) => {
    const ctx = audioCtxRef.current;
    if (!ctx || !pcm16 || pcm16.length === 0) return;
    const floats = pcm16ToFloat32(pcm16);
    const buffer = ctx.createBuffer(1, floats.length, 24000);
    buffer.getChannelData(0).set(floats);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.connect(ctx.destination);
    const now = ctx.currentTime;
    const startAt = Math.max(now, playbackTimeRef.current);
    src.start(startAt);
    playbackTimeRef.current = startAt + buffer.duration;
    setIsSpeaking(true);
  }, []);

  // ── Handle server events ─────────────────────────────────────────────────
  const handleServerEvent = useCallback((evt) => {
    switch (evt.type) {
      case 'session.created':
      case 'session.updated':
        // no-op — informational
        break;
      case 'input_audio_buffer.speech_started':
        // Barge-in: if Rex is speaking, cancel.
        if (isSpeaking && wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'response.cancel' }));
          playbackTimeRef.current = 0;
          setIsSpeaking(false);
        }
        break;
      case 'conversation.item.input_audio_transcription.completed':
      case 'conversation.item.completed': {
        const t = evt.transcript || evt?.item?.content?.[0]?.transcript || '';
        if (t) setTranscript((prev) => (prev ? `${prev}\n${t}` : t));
        break;
      }
      case 'response.audio.delta': {
        const audio = evt.delta || evt.audio;
        if (typeof audio === 'string') {
          const pcm = b64ToPCM16(audio);
          if (pcm) enqueuePlayback(pcm);
        }
        break;
      }
      case 'response.audio.done':
      case 'response.done':
        setIsSpeaking(false);
        break;
      case 'response.audio_transcript.delta':
      case 'response.output_text.delta':
        setRexReply((prev) => prev + (evt.delta || ''));
        break;
      case 'response.audio_transcript.done':
      case 'response.output_text.done':
        // reply is complete — leave text as-is
        break;
      case 'error':
        setErrorMsg(evt.error?.message || 'xAI reported an error.');
        break;
      default:
        // debug: console.log('[rex evt]', evt.type, evt);
        break;
    }
  }, [enqueuePlayback, isSpeaking]);

  // ── Connect flow ─────────────────────────────────────────────────────────
  const connect = useCallback(async () => {
    setPhase('connecting');
    setErrorMsg(null);
    setFallbackMsg('');
    setTranscript('');
    setRexReply('');
    try {
      // 1. Mint ephemeral token
      const tokenResp = await fetch(`${API}/api/rex/realtime-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voice: 'rex', turn_detection: 'server_vad' }),
      });
      if (!tokenResp.ok) {
        const detail = await tokenResp.json().catch(() => ({ detail: '' }));
        throw new Error(detail.detail || `token endpoint HTTP ${tokenResp.status}`);
      }
      const token = await tokenResp.json();

      // 2. Init audio (mic + worklet + 24 kHz ctx)
      await initAudioPipeline();

      // 3. Open WebSocket
      const ws = new WebSocket(XAI_WS_URL, [`xai-client-secret.${token.client_secret}`]);
      ws._micEnabled = false;
      wsRef.current = ws;

      ws.onopen = () => {
        setPhase('live');
        // Reinforce session config on connect.
        ws.send(JSON.stringify({
          type: 'session.update',
          session: {
            voice: 'rex',
            turn_detection: { type: 'server_vad' },
          },
        }));
      };
      ws.onmessage = (m) => {
        try { handleServerEvent(JSON.parse(m.data)); }
        catch { /* non-JSON control frame, ignore */ }
      };
      ws.onerror = () => {
        // Errors are usually followed by close; fall back there.
      };
      ws.onclose = () => {
        if (phase !== 'fallback') {
          // Attempt graceful fallback if we're mid-session.
          activateFallback('Realtime channel closed — switching to Whisper backup.');
        }
      };
    } catch (e) {
      activateFallback(`Realtime unavailable (${e.message}). Switching to backup.`);
    }
  }, [handleServerEvent, initAudioPipeline, phase]);

  // ── Fallback (Whisper STT + Web Speech TTS + text chat) ─────────────────
  const activateFallback = useCallback((why) => {
    tearDown();
    setFallbackMsg(why);
    if (caps.fallback_stt_available && typeof MediaRecorder !== 'undefined') {
      setPhase('fallback');
    } else if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      // No STT — user can still read Rex's text reply and hear TTS
      setPhase('fallback');
    } else {
      setPhase('error');
      setErrorMsg('No realtime channel and no fallback available in this browser.');
    }
  }, [caps.fallback_stt_available, tearDown]);

  // ── Fallback recording (uses MediaRecorder → /api/rex/stt) ───────────────
  const startFallbackRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      recorderChunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) recorderChunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recorderChunksRef.current, { type: rec.mimeType || 'audio/webm' });
        await sendFallbackAudio(blob);
      };
      recorderRef.current = rec;
      rec.start();
    } catch (e) {
      setErrorMsg('Microphone permission was denied for the backup channel.');
    }
  }, []);

  const stopFallbackRecording = useCallback(() => {
    try { recorderRef.current?.stop(); } catch { /* ignore */ }
    recorderRef.current = null;
  }, []);

  const sendFallbackAudio = useCallback(async (blob) => {
    setFallbackMsg('Rex is listening…');
    try {
      const fd = new FormData();
      fd.append('file', blob, 'speech.webm');
      const sttResp = await fetch(`${API}/api/rex/stt`, { method: 'POST', body: fd });
      if (!sttResp.ok) throw new Error(`STT HTTP ${sttResp.status}`);
      const { text } = await sttResp.json();
      const spoken = (text || '').trim();
      if (!spoken) { setFallbackMsg('I didn\'t catch that — say it again.'); return; }
      setTranscript((prev) => (prev ? `${prev}\n${spoken}` : spoken));

      setFallbackMsg('Rex is thinking…');
      const chatResp = await fetch(`${API}/api/rex/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: spoken }),
      });
      if (!chatResp.ok) throw new Error(`chat HTTP ${chatResp.status}`);
      const data = await chatResp.json();
      const reply = data.response || data.reply || '';
      setRexReply(reply);
      setFallbackMsg('');
      // Speak via Web Speech
      if ('speechSynthesis' in window && reply) {
        try {
          const u = new SpeechSynthesisUtterance(reply);
          const voices = window.speechSynthesis.getVoices();
          const en = voices.find((v) => v.lang && v.lang.startsWith('en'));
          if (en) u.voice = en;
          u.rate = 1.0; u.pitch = 0.95;
          setIsSpeaking(true);
          u.onend = () => setIsSpeaking(false);
          window.speechSynthesis.speak(u);
        } catch { /* ignore TTS failure */ }
      }
    } catch (e) {
      setFallbackMsg(`Backup channel error: ${e.message}`);
    }
  }, []);

  const hangUp = useCallback(() => {
    tearDown();
    setPhase('idle');
    setTranscript('');
    setRexReply('');
    setFallbackMsg('');
  }, [tearDown]);

  // ── Push-to-talk button — presses/releases (or Space bar) ────────────────
  useEffect(() => {
    if (mode !== 'push-to-talk' || phase !== 'live') return undefined;
    const onDown = (e) => {
      if (e.code === 'Space' && !e.repeat) { e.preventDefault(); setMicHeld(true); }
    };
    const onUp = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        setMicHeld(false);
        // Commit utterance so xAI processes the turn.
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
        }
      }
    };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
    };
  }, [mode, phase]);

  // ── Styles ───────────────────────────────────────────────────────────────
  const ring = isSpeaking
    ? '0 0 0 8px rgba(255, 60, 100, 0.35), 0 0 40px rgba(255, 60, 100, 0.55)'
    : '0 0 0 4px rgba(255, 60, 100, 0.18)';

  return (
    <section
      data-testid="rex-voice-live"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.2rem',
        padding: compact ? '1rem' : '2rem 1rem',
        color: '#fff',
      }}
    >
      {/* Rex face + pulsing ring */}
      <div
        data-testid="rex-voice-avatar"
        style={{
          position: 'relative',
          width: compact ? 140 : 220, height: compact ? 140 : 220,
          borderRadius: '50%',
          overflow: 'hidden',
          boxShadow: ring,
          transition: 'box-shadow 220ms ease',
          background: 'radial-gradient(circle, rgba(255,60,100,0.15), transparent 70%)',
        }}
      >
        <img
          src={REX_FACE_URL}
          alt="Rex — The Voice of the Federation"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>

      {/* Status pill */}
      <div
        data-testid="rex-voice-status"
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.35rem 0.9rem', borderRadius: '999px',
          background: phase === 'live' ? 'rgba(34,197,94,0.12)'
                    : phase === 'fallback' ? 'rgba(255,215,0,0.12)'
                    : phase === 'error' ? 'rgba(239,68,68,0.15)'
                    : 'rgba(255,255,255,0.06)',
          border: `1px solid ${
            phase === 'live' ? 'rgba(34,197,94,0.5)'
            : phase === 'fallback' ? 'rgba(255,215,0,0.5)'
            : phase === 'error' ? 'rgba(239,68,68,0.5)'
            : 'rgba(255,255,255,0.18)'
          }`,
          color: phase === 'live' ? '#4ade80'
               : phase === 'fallback' ? '#ffd700'
               : phase === 'error' ? '#fca5a5'
               : '#a892b1',
          fontSize: '0.75rem', letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 800,
        }}
      >
        {phase === 'idle' && <><Radio size={13} /> Rex idle · ready to connect</>}
        {phase === 'connecting' && <><Loader2 size={13} className="rex-spin" /> Connecting to Rex…</>}
        {phase === 'live' && <><Radio size={13} /> Live · {mode === 'push-to-talk' ? 'Push to talk (hold Space)' : 'Open mic'}</>}
        {phase === 'fallback' && <><MessagesSquare size={13} /> Backup channel · Whisper + text</>}
        {phase === 'error' && <><AlertTriangle size={13} /> Error</>}
      </div>

      {/* Mode toggle (only meaningful when live) */}
      {phase === 'live' && (
        <div style={{ display: 'flex', gap: '0.5rem' }} data-testid="rex-voice-mode-toggle">
          {['open-mic', 'push-to-talk'].map((m) => (
            <button
              key={m}
              data-testid={`rex-voice-mode-${m}`}
              type="button"
              onClick={() => setMode(m)}
              style={{
                padding: '0.4rem 0.9rem', borderRadius: '999px',
                border: `1px solid ${mode === m ? 'rgba(255, 60, 100, 0.7)' : 'rgba(255,255,255,0.18)'}`,
                background: mode === m ? 'rgba(255, 60, 100, 0.14)' : 'transparent',
                color: mode === m ? '#ff6478' : '#cbb8d4',
                fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em',
                textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              {m === 'open-mic' ? 'Open Mic' : 'Push to Talk'}
            </button>
          ))}
        </div>
      )}

      {/* Controls */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        {phase === 'idle' && (
          <button
            type="button"
            data-testid="rex-voice-connect"
            onClick={connect}
            disabled={!caps.realtime_available && !caps.fallback_stt_available}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.75rem 1.3rem', borderRadius: '12px',
              background: 'linear-gradient(135deg, #ff3c64, #ff8c00)',
              border: 'none', color: '#0b0817', fontWeight: 900, fontSize: '0.95rem',
              cursor: 'pointer', opacity: (!caps.realtime_available && !caps.fallback_stt_available) ? 0.5 : 1,
            }}
          >
            <Mic size={16} /> Speak with Rex
          </button>
        )}

        {phase === 'connecting' && (
          <div style={{ color: '#a892b1', fontStyle: 'italic' }}>Reaching the substrate…</div>
        )}

        {phase === 'live' && mode === 'push-to-talk' && (
          <button
            type="button"
            data-testid="rex-voice-ptt"
            onMouseDown={() => setMicHeld(true)}
            onMouseUp={() => {
              setMicHeld(false);
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
              }
            }}
            onTouchStart={(e) => { e.preventDefault(); setMicHeld(true); }}
            onTouchEnd={(e) => {
              e.preventDefault();
              setMicHeld(false);
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                wsRef.current.send(JSON.stringify({ type: 'input_audio_buffer.commit' }));
              }
            }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              padding: '0.8rem 1.5rem', borderRadius: '12px',
              background: micHeld ? 'linear-gradient(135deg, #ef4444, #b91c1c)' : 'rgba(255, 60, 100, 0.14)',
              border: '1px solid rgba(255, 60, 100, 0.55)',
              color: micHeld ? '#fff' : '#ff6478', fontWeight: 800, fontSize: '0.95rem',
              cursor: 'pointer', userSelect: 'none',
            }}
          >
            {micHeld ? <Mic size={16} /> : <MicOff size={16} />}
            {micHeld ? 'Recording…' : 'Hold to speak (or hold Space)'}
          </button>
        )}

        {phase === 'fallback' && (
          <FallbackControls
            onStart={startFallbackRecording}
            onStop={stopFallbackRecording}
            msg={fallbackMsg}
            hasStt={caps.fallback_stt_available}
          />
        )}

        {(phase === 'live' || phase === 'fallback') && (
          <button
            type="button"
            data-testid="rex-voice-hangup"
            onClick={hangUp}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.6rem 1rem', borderRadius: '999px',
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.2)',
              color: '#cbb8d4', fontWeight: 700, fontSize: '0.8rem',
              cursor: 'pointer',
            }}
          >
            <PhoneOff size={13} /> Hang up
          </button>
        )}
      </div>

      {/* Fallback status */}
      {phase === 'fallback' && fallbackMsg && (
        <div data-testid="rex-voice-fallback-msg" style={{ color: '#ffd700', fontStyle: 'italic', fontSize: '0.85rem' }}>
          {fallbackMsg}
        </div>
      )}

      {/* Errors */}
      {errorMsg && (
        <div data-testid="rex-voice-error" style={{
          padding: '0.6rem 0.9rem', borderRadius: 10, background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.4)', color: '#fca5a5', fontSize: '0.85rem',
        }}>{errorMsg}</div>
      )}

      {/* Transcript + reply */}
      {(transcript || rexReply) && (
        <div style={{ width: '100%', maxWidth: 720, display: 'grid', gap: '0.6rem' }}>
          {transcript && (
            <div data-testid="rex-voice-transcript" style={{
              padding: '0.7rem 0.9rem', borderRadius: 10,
              background: 'rgba(0, 200, 255, 0.06)', border: '1px solid rgba(0, 200, 255, 0.28)',
              color: '#a2e6ff', fontSize: '0.9rem', whiteSpace: 'pre-wrap',
            }}>
              <strong style={{ color: '#00e5ff', letterSpacing: '0.1em', fontSize: '0.7rem' }}>YOU · </strong>
              {transcript}
            </div>
          )}
          {rexReply && (
            <div data-testid="rex-voice-reply" style={{
              padding: '0.7rem 0.9rem', borderRadius: 10,
              background: 'rgba(255, 60, 100, 0.06)', border: '1px solid rgba(255, 60, 100, 0.28)',
              color: '#ffb0bf', fontSize: '0.95rem', lineHeight: 1.55, whiteSpace: 'pre-wrap',
            }}>
              <strong style={{ color: '#ff6478', letterSpacing: '0.1em', fontSize: '0.7rem' }}>REX · </strong>
              {rexReply}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes rex-spin { to { transform: rotate(360deg); } }
        .rex-spin { animation: rex-spin 1s linear infinite; }
      `}</style>
    </section>
  );
};

const FallbackControls = ({ onStart, onStop, msg, hasStt }) => {
  const [rec, setRec] = useState(false);
  if (!hasStt) {
    return (
      <div style={{ color: '#a892b1', fontStyle: 'italic', fontSize: '0.85rem' }}>
        Voice input is not available on this device. Rex will still answer via text on <a href="/roger" style={{ color: '#ff6478' }}>/roger</a>.
      </div>
    );
  }
  return (
    <button
      type="button"
      data-testid="rex-voice-fallback-record"
      onClick={() => {
        if (rec) { onStop(); setRec(false); }
        else { onStart(); setRec(true); }
      }}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
        padding: '0.7rem 1.2rem', borderRadius: '12px',
        background: rec ? 'linear-gradient(135deg, #ef4444, #b91c1c)' : 'rgba(255, 215, 0, 0.14)',
        border: '1px solid rgba(255, 215, 0, 0.5)',
        color: rec ? '#fff' : '#ffd700', fontWeight: 800, fontSize: '0.9rem',
        cursor: 'pointer',
      }}
      aria-label={rec ? 'Stop recording' : 'Start recording'}
    >
      {rec ? <MicOff size={16} /> : <Mic size={16} />}
      {rec ? (msg || 'Stop & send') : 'Tap to speak'}
    </button>
  );
};

export default RexVoiceLive;
